import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiData, ApiResponse } from 'src/app/core/interfaces/api/api-response.interface';
import { environment } from 'src/environments/environment';
import { CasinoStatistics } from '../../interfaces/api/casino.interface';
import {
  BettingCategoryStats,
  BonusPromotionalCode,
  cancelTicket,
  CasinoBetReport,
  DownloadReportRequest,
  GeneralCasionoReportByAgent,
  GroupedLoginAttemptBackOffice,
  GroupedSportsBetting,
  LoginAttemptBackOffice,
  LoginAttemptPlayer,
  PlayerReport,
  ProviderReport,
  ReportExport,
  ReportHistoryItem,
  SpecialBet,
  SportsBetting,
  ticketResponse,
  VirtualBet,
} from '../../interfaces/api/report.interface';
import { ToastrNotificationService } from '../ui/toastr-notification.service';
import { EncryptionService } from '../ui/encryption.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  private apiUrl: string = `${environment.apiUrl}/report`;
  private apiReportsUrl: string = `${environment.apiReportsUrl}/report`;
  private apiDatamartUrl: string = `${environment.apiReportsUrl}/datamart`;
  private apiUrlMTS: string = `${environment.apiUrl}/tickets-mts`;

  constructor(
    private _httpClient: HttpClient,
    private _notificationService: ToastrNotificationService,
    private _encryptionService: EncryptionService,
    private _translate: TranslateService
  ) {}

  public findBettingReport(filter: object): Observable<ApiResponse<ApiData<SportsBetting[]>>> {
    const endpoint = `${this.apiUrl}/sports-betting`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<SportsBetting[]>>>(endpoint, { params });
  }

  public findSportsReport(
    filter: object
  ): Observable<ApiResponse<ApiData<GroupedSportsBetting[]>>> {
    const endpoint = `${this.apiUrl}/sports-betting/sports`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<GroupedSportsBetting[]>>>(endpoint, { params });
  }

  public findCategoriesReport(
    filter: object
  ): Observable<ApiResponse<ApiData<GroupedSportsBetting[]>>> {
    const endpoint = `${this.apiUrl}/sports-betting/categories`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<GroupedSportsBetting[]>>>(endpoint, { params });
  }

  public findEventsReport(filter: object): Observable<ApiResponse<ApiData<any[]>>> {
    const endpoint = `${this.apiUrl}/sports-betting/events`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<GroupedSportsBetting[]>>>(endpoint, { params });
  }

  public findMarketsReport(filter: object): Observable<ApiResponse<ApiData<any[]>>> {
    const endpoint = `${this.apiUrl}/sports-betting/markets`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<GroupedSportsBetting[]>>>(endpoint, { params });
  }

  public getReportByCasimoGames(filter: object): Observable<ApiResponse<ApiData<any[]>>> {
    const endpoint = `${this.apiReportsUrl}/casino/report-by-games`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<any[]>>>(endpoint, {
      params,
    });
  }

  public getReportByCurrency(filter: object): Observable<ApiResponse<ApiData<CasinoStatistics[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/casino/report-by-currencies`;
    return this._httpClient.get<ApiResponse<ApiData<CasinoStatistics[]>>>(endpoint, {
      params,
    });
  }

  public getReportByBets(filter: object): Observable<ApiResponse<ApiData<any[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/casino/report-by-bets`;
    return this._httpClient.get<ApiResponse<ApiData<any[]>>>(endpoint, {
      params,
    });
  }

  public getReportByPlayers(filter: object): Observable<ApiResponse<ApiData<any[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/casino/report-by-players`;
    return this._httpClient.get<ApiResponse<ApiData<any[]>>>(endpoint, {
      params,
    });
  }

  public getReportByProvider(filter: object): Observable<ApiResponse<ApiData<any[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/casino/report-by-provider`;
    return this._httpClient.get<ApiResponse<ApiData<any[]>>>(endpoint, {
      params,
    });
  }

  public getBackOfficeSessionHistoryReport(
    filter: object
  ): Observable<ApiResponse<ApiData<LoginAttemptBackOffice[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/login-histories/backoffice`;
    return this._httpClient.get<ApiResponse<ApiData<LoginAttemptBackOffice[]>>>(endpoint, {
      params,
    });
  }

  public getBackOfficeSessionHistoryGrouped(
    filter: object
  ): Observable<ApiResponse<ApiData<GroupedLoginAttemptBackOffice[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/login-histories/backoffice`;
    return this._httpClient.get<ApiResponse<ApiData<GroupedLoginAttemptBackOffice[]>>>(
      endpoint,
      {
        params,
      }
    );
  }

  public getPlayerSessionHistoryReport(
    filter: object
  ): Observable<ApiResponse<ApiData<LoginAttemptPlayer[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/login-histories/player`;
    return this._httpClient.get<ApiResponse<ApiData<LoginAttemptPlayer[]>>>(endpoint, {
      params,
    });
  }

  public getPlayerSessionHistoryGrouped(
    filter: object
  ): Observable<ApiResponse<ApiData<GroupedLoginAttemptBackOffice[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/login-histories/player`;
    return this._httpClient.get<ApiResponse<ApiData<GroupedLoginAttemptBackOffice[]>>>(
      endpoint,
      {
        params,
      }
    );
  }

  getAllGeneralProductsReport(
    filter: object
  ): Observable<ApiResponse<ApiData<BettingCategoryStats[]>>> {
    const endpoint = `${this.apiReportsUrl}/general-products`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<BettingCategoryStats[]>>>(endpoint, {
      params,
    });
  }

  getAllAgentStats(filter: object): Observable<ApiResponse<ApiData<BettingCategoryStats[]>>> {
    const endpoint = `${this.apiReportsUrl}/agent-stats`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<BettingCategoryStats[]>>>(endpoint, {
      params,
    });
  }

  getAllAgentReportsDatamart(
    filter: object
  ): Observable<ApiResponse<ApiData<BettingCategoryStats[]>>> {
    const endpoint = `${this.apiDatamartUrl}/agent-reports-datamart`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<BettingCategoryStats[]>>>(endpoint, {
      params,
    });
  }

  getAllPlayerReportsDatamart(
    filter: object
  ): Observable<ApiResponse<ApiData<BettingCategoryStats[]>>> {
    const endpoint = `${this.apiDatamartUrl}/player-reports-datamart`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<BettingCategoryStats[]>>>(endpoint, {
      params,
    });
  }

  getAllPlayerStats(filter: object): Observable<ApiResponse<ApiData<BettingCategoryStats[]>>> {
    const endpoint = `${this.apiReportsUrl}/player-stats`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<BettingCategoryStats[]>>>(endpoint, {
      params,
    });
  }

  public getBonusPromotionalCodeReport(
    filter: object
  ): Observable<ApiResponse<ApiData<BonusPromotionalCode[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/bonus`;
    return this._httpClient.get<ApiResponse<ApiData<BonusPromotionalCode[]>>>(endpoint, {
      params,
    });
  }

  public getReportLiveCasinoByBets(
    filter: object
  ): Observable<ApiResponse<ApiData<CasinoBetReport[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/live-casino/report-by-bets`;
    return this._httpClient.get<ApiResponse<ApiData<CasinoBetReport[]>>>(endpoint, {
      params,
    });
  }

  public getReportByLiveCasimoGames(filter: object): Observable<ApiResponse<ApiData<any[]>>> {
    const endpoint = `${this.apiReportsUrl}/live-casino/report-by-games`;
    const params = new HttpParams({ fromObject: { ...filter } });
    return this._httpClient.get<ApiResponse<ApiData<any[]>>>(endpoint, {
      params,
    });
  }

  public getReportByLiveCasinoCurrency(
    filter: object
  ): Observable<ApiResponse<ApiData<CasinoStatistics[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/live-casino/report-by-currencies`;
    return this._httpClient.get<ApiResponse<ApiData<CasinoStatistics[]>>>(endpoint, {
      params,
    });
  }

  public getReportByLiveCasinoPlayers(
    filter: object
  ): Observable<ApiResponse<ApiData<PlayerReport[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/live-casino/report-by-players`;
    return this._httpClient.get<ApiResponse<ApiData<PlayerReport[]>>>(endpoint, {
      params,
    });
  }

  public getReportByLiveCasinoProviders(
    filter: object
  ): Observable<ApiResponse<ApiData<ProviderReport[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/live-casino/report-by-provider`;
    return this._httpClient.get<ApiResponse<ApiData<ProviderReport[]>>>(endpoint, {
      params,
    });
  }
  public getCasinoReportForGeneralAgents(
    product: string | null,
    filter: object
  ): Observable<ApiResponse<ApiData<GeneralCasionoReportByAgent[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiUrl}/general-products/${product}`;
    return this._httpClient.get<ApiResponse<ApiData<GeneralCasionoReportByAgent[]>>>(endpoint, {
      params,
    });
  }

  public cancelTicket(
    _cancelTicket: cancelTicket,
    stake: {
      type: string;
      currency: string | undefined;
      amount: string | undefined;
    }[],
    playerId: string | undefined
  ) {
    const data = this._encryptionService.encrypt(
      JSON.stringify({
        ..._cancelTicket,
        stake,
        playerId,
      })
    );
    return this._httpClient.post<ApiResponse<ticketResponse>>(
      `${this.apiUrlMTS}/cancel-ticket-admin`,
      {
        data,
      }
    );
  }

  public downloadReport(request: DownloadReportRequest): Observable<ApiResponse<ReportExport>> {
    const endpoint = `${this.apiUrl}/download`;
    const language = this._translate.currentLang;
    const headers = new HttpHeaders({
      'Accept-Language': language,
    });
    return this._httpClient.post<ApiResponse<ReportExport>>(endpoint, request, {
      headers,
    });
  }

  public getReportHistory(filter: object): Observable<ApiResponse<ApiData<ReportHistoryItem[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiUrl}/get-all-history`;
    return this._httpClient.get<ApiResponse<ApiData<ReportHistoryItem[]>>>(endpoint, { params });
  }

  public getAllSpecialsBets(filter: object): Observable<ApiResponse<ApiData<SpecialBet[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/specials-bets`;
    return this._httpClient.get<ApiResponse<ApiData<SpecialBet[]>>>(endpoint, {
      params,
    });
  }

  public getAllVirtualBets(filter: object): Observable<ApiResponse<ApiData<VirtualBet[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.apiReportsUrl}/virtual-bets`;
    return this._httpClient.get<ApiResponse<ApiData<VirtualBet[]>>>(endpoint, {
      params,
    });
  }
}
